/**
 * ReturnLink — the commons-side return element (DR-2026-272 return-
 * navigation wave; "Commons tools row" card, LJUSA TIDER Fleet Design
 * System, is the visual spec of record).
 *
 * A visitor who reaches the guides commons via a tenant's Guides entry
 * arrives with ?from=<tenantKey>. This component validates the key against
 * the addon-shipped fleet registry (key → public origin + display label),
 * persists it in sessionStorage (per-tab; survives navigation deep into
 * tutorial chapters — exactly where browser-back dies), strips the param
 * from the address bar, and renders `← <label>` back to the tenant for the
 * rest of the tab session.
 *
 * The query value is NEVER rendered and NEVER used as a URL — only
 * registry-known origins can appear (closes the open-redirect / spoofing
 * surface an URL-carrying param would open). Unknown keys, direct visits,
 * and bookmarks render nothing. Renders only where isCommons is set (the
 * guides tenant); everywhere else it is inert.
 *
 * SSR renders null (all window/sessionStorage access lives in the mount
 * effect); the element hydrates client-side. Storage failures degrade
 * gracefully: the element still renders for the param-carrying page view
 * and simply does not persist.
 */
import { useEffect, useState } from 'react';
import config from '@plone/volto/registry';

const STORAGE_KEY = 'tenantBaseline.returnTo';

const ReturnLink = () => {
  const [tenantKey, setTenantKey] = useState(null);
  const tb = config.settings.tenantBaseline || {};
  const registry = tb.fleetRegistry || {};
  const isCommons = !!tb.isCommons;

  useEffect(() => {
    if (!isCommons) return;
    let key = null;
    try {
      const url = new URL(window.location.href);
      const fromParam = url.searchParams.get('from');
      if (fromParam && registry[fromParam]) {
        key = fromParam;
        try {
          window.sessionStorage.setItem(STORAGE_KEY, key);
        } catch {
          // storage unavailable — render for this page view, skip persistence
        }
        url.searchParams.delete('from');
        window.history.replaceState(
          null,
          '',
          url.pathname + url.search + url.hash,
        );
      }
      if (!key) {
        try {
          const stored = window.sessionStorage.getItem(STORAGE_KEY);
          if (stored && registry[stored]) key = stored;
        } catch {
          // storage unavailable — nothing persisted, nothing to restore
        }
      }
    } catch {
      // the header must never break on a malformed URL
    }
    if (key) setTenantKey(key);
    // registry/isCommons come from the config singleton — stable per build
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCommons]);

  if (!isCommons || !tenantKey) return null;
  const entry = registry[tenantKey];
  if (!entry || !entry.origin) return null;

  return (
    <a
      className="tenant-baseline-return-link"
      href={entry.origin}
      rel="noopener"
    >
      ← {entry.label}
    </a>
  );
};

export default ReturnLink;
